import { Component, EventEmitter, Input, Optional, Output } from '@angular/core';
import { AGURenderService, PercentLabel } from 'agu_app_shell';
import { RA_MODULE_KEYS } from '../../ra-module-keys';
import { ExplorerRaHostAdapter } from '../../ra-explorer-host.adapter';
import { ResearchCareerMatches, ResearchCvTag, ResearchJobProfile, ResearchMemberCv, ResearchRoleMatch } from '../../models/research.models';
import {
    ConfirmedChip, ConfirmedChips, ConfirmedCountsWith, MoreRoles, ReviewCard, ReviewCategories, ReviewCategory, ReviewCategoryLabels,
    ParseOnlyMessage, ReviewCategoryNouns, RoleCardStats, SeenTag, SeenVocabulary, TabRing, TabRings, TopRoles,
} from './career-explorer.state';

/** P6:1619 `S.more[..] ? all : 9`: chips shown before "+n more". */
const CHIPS_BEFORE_MORE = 9;

/** The text under the picker for a name the vocabulary does not hold (P6:1629 has no such state; see `Add`). */
export const NOT_IN_VOCABULARY = 'Not in the vocabulary yet';
/** Shown under the picker when the server did not take the tag; the typed name stays. */
export const ADD_FAILED = 'We could not add that. Try again.';

export interface MoreRoleRow {
    readonly Match: ResearchRoleMatch;
    /** The median salary, or the job family when the role has none. */
    readonly Sub: string;
    readonly Percent: number;
    readonly PercentLabel: string;
}

interface VocabularyIndex {
    readonly matches: ResearchCareerMatches | null;
    readonly byName: ReadonlyMap<string, SeenTag & { name: string }>;
    readonly byLower: ReadonlyMap<string, SeenTag & { name: string }>;
}

export interface CategoryViewModel {
    readonly Pending: readonly ReviewCard[];
    readonly PendingShown: readonly ReviewCard[];
    readonly PendingMore: number;
    readonly Confirmed: readonly ConfirmedChip[];
    readonly ConfirmedShown: readonly ConfirmedChip[];
    readonly ConfirmedMore: number;
}

/**
 * The Career Explorer ready view (P6:1600-1638 `vReady`): the top three role cards, "Sharpen your results", the Your CV card and
 * "More roles to explore". Presentational: the page owns the data and sends every change (review decisions, asserted tags, replace,
 * delete) to the server.
 *
 * Deviations from P6 (each also commented where it applies):
 * - one score per role, not the P6:1600-1638 range, and no Experience tile (see `CxRoleCardComponent`);
 * - the vocabulary picker (P6:405-409, 1629) lists the vocabulary the matches mention that the member has not confirmed, and a
 *   name outside it shows "Not in the vocabulary yet" instead of being accepted as free text (the server takes a tag id, not a word).
 */
@Component({
    standalone: false,
    selector: 'ra-cx-ready',
    templateUrl: './cx-ready.component.html',
    styleUrl: './cx-ready.component.scss',
})
export class CxReadyComponent {
    @Input() Matches: ResearchCareerMatches | null = null;
    @Input() Profiles: ReadonlyMap<string, ResearchJobProfile> = new Map();
    /** Parse-only provider: the role cards give way to the calm panel; Sharpen and Your CV stay (spec line 201). */
    @Input() ParseOnly = false;
    /** The panel copy for parse-only; null shows no panel. */
    @Input() ParseOnlyPanel: ParseOnlyMessage | null = null;
    @Input() Cv: ResearchMemberCv | null = null;
    @Input() Tags: readonly ResearchCvTag[] = [];
    /** The review cards with decisions still in flight applied (the page's memoized `ReviewCards`). */
    @Input() Cards: readonly ReviewCard[] = [];
    /** The short-lived PDF link the thumbnail reads; null until the page has one. */
    @Input() DownloadUrl: string | null = null;
    /** The page's answer to the last `Assert`: success clears the typed name, failure keeps it and says so. A new object each time. */
    @Input() set AssertOutcome(outcome: { ok: boolean } | null) {
        if (outcome === null) return;
        if (outcome.ok) {
            this.pickerValue = '';
            this.pickerError = null;
        } else {
            this.pickerError = ADD_FAILED;
        }
        this.render?.Notify();
    }

    @Output() readonly ExploreAll = new EventEmitter<void>();
    @Output() readonly Review = new EventEmitter<ReviewCategory>();
    @Output() readonly Accept = new EventEmitter<string[]>();
    @Output() readonly Decline = new EventEmitter<string[]>();
    @Output() readonly Assert = new EventEmitter<string>();
    @Output() readonly Retract = new EventEmitter<string>();
    /** Undo an approval from the Confirmed box: the card id and its chain members; the tag returns to To review (P6:1811). */
    @Output() readonly Undo = new EventEmitter<string[]>();
    @Output() readonly Replace = new EventEmitter<void>();
    @Output() readonly Delete = new EventEmitter<void>();

    readonly Categories = ReviewCategories;
    Editing = false;
    pickerValue = '';
    pickerError: string | null = null;

    private chosen: ReviewCategory | null = null;
    private readonly expanded = new Set<string>();
    private expandedVersion = 0;

    private topMemo: { matches: ResearchCareerMatches | null; value: ResearchRoleMatch[] } | null = null;
    private moreMemo: { matches: ResearchCareerMatches | null; profiles: ReadonlyMap<string, ResearchJobProfile>; value: MoreRoleRow[] } | null = null;
    private ringsMemo: { cards: readonly ReviewCard[]; value: TabRing[] } | null = null;
    private countsMemo: { tags: readonly ResearchCvTag[]; cards: readonly ReviewCard[]; value: { confirmed: number; total: number } } | null = null;
    private vocabMemo: VocabularyIndex | null = null;
    private viewMemo: { tags: readonly ResearchCvTag[]; cards: readonly ReviewCard[]; category: ReviewCategory; version: number; value: CategoryViewModel } | null = null;
    private pickerMemo: { matches: ResearchCareerMatches | null; tags: readonly ResearchCvTag[]; cards: readonly ReviewCard[]; category: ReviewCategory; value: string[] } | null = null;

    constructor(private readonly host: ExplorerRaHostAdapter, @Optional() private readonly render: AGURenderService | null = null) {}

    // ── Parse-only switches ──────────────────────────────────────────────

    get ShowRoleCards(): boolean { return !this.ParseOnly; }
    get ShowExploreAll(): boolean { return !this.ParseOnly; }
    get ShowMoreRoles(): boolean { return !this.ParseOnly && this.MoreRolesList.length > 0; }
    get ShowPanel(): boolean { return this.ParseOnly && this.ParseOnlyPanel !== null; }
    /** The picker needs the matches' vocabulary; with none (parse-only) the row is hidden, the chips still work from the CV tags. */
    get ShowPicker(): boolean { return this.Vocabulary.byName.size > 0; }

    // ── Roles ────────────────────────────────────────────────────────────

    /** The three role cards, in rank order. Memoized on the matches object (the template reads it every pass). */
    get TopRoles(): ResearchRoleMatch[] {
        if (this.topMemo === null || this.topMemo.matches !== this.Matches) {
            this.topMemo = { matches: this.Matches, value: this.Matches ? TopRoles(this.Matches) : [] };
        }
        return this.topMemo.value;
    }

    /** "More roles to explore" rows (P6:1630): title, salary, 50 px ring. */
    get MoreRolesList(): MoreRoleRow[] {
        if (this.moreMemo === null || this.moreMemo.matches !== this.Matches || this.moreMemo.profiles !== this.Profiles) {
            const rows = (this.Matches ? MoreRoles(this.Matches) : []).map((m): MoreRoleRow => {
                const profile = this.Profiles.get(m.roleRef);
                const label = PercentLabel(m.overlap);
                return { Match: m, Sub: (profile ? RoleCardStats(profile).salary : null) ?? m.jobFamily, Percent: Number.parseInt(label, 10), PercentLabel: label };
            });
            this.moreMemo = { matches: this.Matches, profiles: this.Profiles, value: rows };
        }
        return this.moreMemo.value;
    }

    ProfileOf(roleRef: string): ResearchJobProfile | null {
        return this.Profiles.get(roleRef) ?? null;
    }

    OpenRole(roleRef: string): void {
        this.host.NavigateTo({ ModuleKey: RA_MODULE_KEYS.CareerMap, View: 'role', RecordID: roleRef });
    }

    // ── Sharpen your results ─────────────────────────────────────────────

    /** "{confirmed} of {total}" (P6:1531-1534, 1621). */
    get Counts(): { confirmed: number; total: number } {
        if (this.countsMemo === null || this.countsMemo.tags !== this.Tags || this.countsMemo.cards !== this.Cards) {
            this.countsMemo = { tags: this.Tags, cards: this.Cards, value: ConfirmedCountsWith(this.Tags, this.Cards) };
        }
        return this.countsMemo.value;
    }

    get TabRings(): TabRing[] {
        if (this.ringsMemo === null || this.ringsMemo.cards !== this.Cards) this.ringsMemo = { cards: this.Cards, value: TabRings(this.Cards) };
        return this.ringsMemo.value;
    }

    /** The tab in view: the member's choice, else the first with tags still to review, else the first (as the review view does). */
    get Category(): ReviewCategory {
        if (this.chosen !== null) return this.chosen;
        const first = this.TabRings.find((t) => t.Pending > 0)?.Category ?? ReviewCategories[0];
        // Settled once the cards have arrived (P6:1481 fixes the tab; only a tab click changes it, P6:1807), so reviewing the
        // last tag of a tab leaves the member on its "All reviewed" state rather than jumping to another tab.
        if (this.Cards.length > 0) this.chosen = first;
        return first;
    }

    LabelOf(c: ReviewCategory): string { return ReviewCategoryLabels[c]; }

    /** P6:1622 `catOne`: the singular noun the picker names ("Add your own skill"). */
    get Noun(): string { return ReviewCategoryNouns[this.Category].toLowerCase(); }

    SelectCategory(c: ReviewCategory): void {
        this.chosen = c;
        this.Editing = false; // P6:1807
        this.pickerError = null;
        this.render?.Notify();
    }

    get CategoryView(): CategoryViewModel {
        const category = this.Category;
        const m = this.viewMemo;
        if (m === null || m.tags !== this.Tags || m.cards !== this.Cards || m.category !== category || m.version !== this.expandedVersion) {
            const pending = this.Cards.filter((c) => c.category === category && c.reviewStatus === 'unreviewed');
            const confirmed = ConfirmedChips(this.Tags, this.Cards, category);
            const pendingCap = this.expanded.has(`${category}p`) ? pending.length : CHIPS_BEFORE_MORE;
            const confirmedCap = this.expanded.has(`${category}c`) ? confirmed.length : CHIPS_BEFORE_MORE;
            const value: CategoryViewModel = {
                Pending: pending, PendingShown: pending.slice(0, pendingCap), PendingMore: Math.max(0, pending.length - pendingCap),
                Confirmed: confirmed, ConfirmedShown: confirmed.slice(0, confirmedCap), ConfirmedMore: Math.max(0, confirmed.length - confirmedCap),
            };
            this.viewMemo = { tags: this.Tags, cards: this.Cards, category, version: this.expandedVersion, value };
            // Nothing left to remove: Edit mode ends (P6:1807 `S.editing=false`), so the next chip does not arrive with its × showing.
            if (!confirmed.some((c) => c.Own || c.Approved)) this.Editing = false;
            return value;
        }
        return m.value;
    }

    /** "+n more" (P6:1619): `p` expands To review, `c` expands Confirmed, for the tab in view. */
    ShowMore(box: 'p' | 'c'): void {
        this.expanded.add(`${this.Category}${box}`);
        this.expandedVersion += 1;
        this.render?.Notify();
    }

    AcceptCard(card: ReviewCard): void { this.Accept.emit([card.providerTagID]); }

    /** One decision per concept: the card and every chain member it stands for (as the review view does, JG section 4). */
    DeclineCard(card: ReviewCard): void { this.Decline.emit([card.providerTagID, ...card.chainMembers]); }

    OpenReview(): void { this.Review.emit(this.Category); }

    // ── Confirmed: edit and the vocabulary picker ────────────────────────

    /** The Edit link shows when a chip can be removed: the member's own, or one approved in review (P6:1626 `c.id || c.own`). */
    get HasRemovable(): boolean { return this.CategoryView.Confirmed.some((c) => c.Own || c.Approved); }

    ToggleEditing(): void { this.Editing = !this.Editing; this.render?.Notify(); }

    RemoveOwn(providerTagID: string): void { this.Retract.emit(providerTagID); }

    /** The `×` on an approved chip (P6:1630 `data-undo`). */
    UndoChip(chip: ConfirmedChip): void { this.Undo.emit([...chip.UndoIds]); }

    private get Vocabulary(): VocabularyIndex {
        if (this.vocabMemo === null || this.vocabMemo.matches !== this.Matches) {
            const byName = new Map<string, SeenTag & { name: string }>();
            const byLower = new Map<string, SeenTag & { name: string }>();
            if (this.Matches) {
                for (const [name, tag] of SeenVocabulary(this.Matches)) {
                    byName.set(name, { ...tag, name });
                    if (!byLower.has(name.toLowerCase())) byLower.set(name.toLowerCase(), { ...tag, name });
                }
            }
            this.vocabMemo = { matches: this.Matches, byName, byLower };
        }
        return this.vocabMemo;
    }

    /** The datalist (P6:1629 `cxOwnList`): this tab's vocabulary the member has not already confirmed, sorted. */
    get PickerNames(): string[] {
        const category = this.Category;
        const m = this.pickerMemo;
        if (m === null || m.matches !== this.Matches || m.tags !== this.Tags || m.cards !== this.Cards || m.category !== category) {
            const confirmed = new Set(ReviewCategories.flatMap((c) => ConfirmedChips(this.Tags, this.Cards, c)).map((c) => c.Name));
            const names = [...this.Vocabulary.byName.values()].filter((v) => v.category === category && !confirmed.has(v.name)).map((v) => v.name).sort();
            this.pickerMemo = { matches: this.Matches, tags: this.Tags, cards: this.Cards, category, value: names };
            return names;
        }
        return m.value;
    }

    OnPickerInput(value: string): void {
        this.pickerValue = value;
        if (this.pickerError !== null) this.pickerError = null;
    }

    /**
     * Adds a vocabulary name as the member's own tag (P6:1625 form). The name resolves (ignoring case and edge space) to the tag id the
     * matches use; a name outside the vocabulary is refused with "Not in the vocabulary yet" (P6 accepted any text; the server cannot).
     */
    Add(name: string = this.pickerValue): void {
        const typed = name.trim();
        if (typed === '') return;
        const found = this.Vocabulary.byName.get(typed) ?? this.Vocabulary.byLower.get(typed.toLowerCase());
        if (!found) {
            this.pickerError = NOT_IN_VOCABULARY;
            this.render?.Notify();
            return;
        }
        // The typed name stays until the page reports success (`AssertOutcome`), so a failed add loses nothing.
        this.pickerError = null;
        this.Assert.emit(found.providerTagID);
        this.render?.Notify();
    }

    // ── Your CV ──────────────────────────────────────────────────────────

    /** The thumbnail needs a PDF and its link; any other saved format has no page to show. */
    get ShowThumbnail(): boolean {
        return this.Cv?.detectedType === 'pdf' && !!this.DownloadUrl;
    }
}
